import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, photoUrl, carTitle, fmtPrice } from "../lib/api";
import { AdminShell } from "../components/admin/AdminShell";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUS = { available: "Disponibile", reserved: "Prenotata", sold: "Venduta" };

const CarRow = ({ car, onPatch, onDelete }) => (
  <div className="flex flex-col gap-4 rounded-2xl border border-ink/10 bg-white p-4 sm:flex-row sm:items-center" data-testid={`admin-car-row-${car.id}`}>
    <div className="h-20 w-full shrink-0 overflow-hidden rounded-xl bg-ink/5 sm:w-28">
      {car.photos?.[0] ? <img src={photoUrl(car.photos[0])} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-slate-400"><ImageOff className="h-5 w-5" /></div>}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate font-display text-lg font-bold uppercase text-ink">{carTitle(car)}</p>
      <p className="truncate text-sm text-slate-600">
        {[car.version, car.year, car.price ? fmtPrice(car.price, "it-IT") : "Prezzo su richiesta", `${car.photos?.length || 0} foto`].filter(Boolean).join(" · ")}
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <select value={car.status} onChange={(e) => onPatch(car, { status: e.target.value })} data-testid={`admin-car-status-${car.id}`}
        className="h-10 rounded-full border border-ink/10 bg-pearl px-4 text-sm">
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        {car.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        <Switch checked={car.visible} onCheckedChange={(v) => onPatch(car, { visible: v })} data-testid={`admin-car-visible-${car.id}`} />
      </label>
      <Link to={`/admin/auto/${car.id}`} className="rounded-full border border-ink/10 p-2.5 hover:bg-ink hover:text-pearl" data-testid={`admin-car-edit-${car.id}`}><Pencil className="h-4 w-4" /></Link>
      <button onClick={() => onDelete(car)} className="rounded-full border border-ink/10 p-2.5 text-red-600 hover:bg-red-600 hover:text-white" data-testid={`admin-car-delete-${car.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [cars, setCars] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => http.get("/admin/cars").then((r) => setCars(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errMsg(e)); setCars([]); });
  useEffect(() => { load(); }, []);

  const patch = async (car, change) => {
    try {
      const { data } = await http.put(`/admin/cars/${car.id}`, { ...car, ...change });
      setCars((cs) => cs.map((c) => (c.id === car.id ? data : c)));
      toast.success("Aggiornato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmDelete = async () => {
    try {
      await http.delete(`/admin/cars/${del.id}`);
      setCars((cs) => cs.filter((c) => c.id !== del.id));
      toast.success("Auto eliminata");
    } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  const stats = [["Totale", cars?.length ?? 0], ["Online", cars?.filter((c) => c.visible).length ?? 0], ["Vendute", cars?.filter((c) => c.status === "sold").length ?? 0]];

  return (
    <AdminShell actions={<Link to="/admin/auto/nuova" className="btn-ink" data-testid="admin-add-car-button"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuova auto</span></Link>}>
      <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-4xl">Le tue vetture</h1>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {stats.map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={`admin-stat-${k.toLowerCase()}`}>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">{k}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-ink">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 space-y-3" data-testid="admin-car-list">
        {cars === null ? <p className="text-slate-600">Caricamento…</p> : cars.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink/20 bg-white p-10 text-center" data-testid="admin-empty">
            <p className="text-slate-700">Nessuna auto inserita.</p>
            <Link to="/admin/auto/nuova" className="btn-ink mt-5 inline-flex"><Plus className="h-4 w-4" /> Inserisci la prima auto</Link>
          </div>
        ) : cars.map((c) => <CarRow key={c.id} car={c} onPatch={patch} onDelete={setDel} />)}
      </div>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {carTitle(del)}?</AlertDialogTitle>
            <AlertDialogDescription>L'auto e le sue foto verranno rimosse dal sito. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
